import { Injectable, inject } from '@angular/core';
import { Subject } from 'rxjs';

import {
  CONTENT_EDITOR_SETTINGS,
  IS_LOGGED_IN,
  JWT_TOKEN_KEY,
  LAST_ACTIVE_CONTEXT_ID,
} from '../constants/app.constants';
import { LOCAL_STORAGE, SESSION_STORAGE, WINDOW } from './browser.tokens';

export interface ContentEditorSettings {
  aceEditorfontSize?: number;
}

export type HierarchyIdsToCopyType = 'copy-product-contents' | 'copy-links';

@Injectable()
export class StorageService {
  private _window = inject<Window>(WINDOW);
  private _localStorage = inject<Storage>(LOCAL_STORAGE);
  private _sessionStorage = inject<Storage>(SESSION_STORAGE);

  tokenRemoved$ = new Subject<void>();

  constructor() {
    // listen for local storage changes, if there was a logout in another browser tab,
    // logout in every other tab
    this._window.addEventListener('storage', () => {
      if (!this.getToken()) this.tokenRemoved$.next();
    });
  }

  saveToken(token: string) {
    if (token) this._localStorage.setItem(JWT_TOKEN_KEY, token);
  }

  removeToken() {
    this._localStorage.removeItem(JWT_TOKEN_KEY);
  }

  getToken() {
    return this._localStorage.getItem(JWT_TOKEN_KEY);
  }

  saveLastActiveContextId(webId: string) {
    this._localStorage.setItem(LAST_ACTIVE_CONTEXT_ID, webId);
  }

  getLastActiveContextId(): string | null {
    return this._localStorage.getItem(LAST_ACTIVE_CONTEXT_ID);
  }

  saveIsLoggedIn() {
    this._sessionStorage.setItem(IS_LOGGED_IN, 'true');
  }

  saveBookmarkUrl(bookmarkUrl: string) {
    this._sessionStorage.setItem('bookmarkUrl', bookmarkUrl);
  }

  getBookmarkUrl(): string | null {
    return this._sessionStorage.getItem('bookmarkUrl');
  }

  removeBookmarkUrl() {
    this._sessionStorage.removeItem('bookmarkUrl');
  }

  removeIsLoggedIn() {
    this._sessionStorage.removeItem(IS_LOGGED_IN);
  }

  getIsLoggedIn(): string | null {
    return this._sessionStorage.getItem(IS_LOGGED_IN);
  }

  saveContentEditorSettings(settings: ContentEditorSettings) {
    this._localStorage.setItem(CONTENT_EDITOR_SETTINGS, JSON.stringify(settings));
  }

  getContentEditorSettings(): ContentEditorSettings {
    return JSON.parse(this._localStorage.getItem(CONTENT_EDITOR_SETTINGS) as string);
  }

  /**
   * In Product list page / in Detail page, we can check multiple rows (based on some conditions) / subordinate links
   * and we can save their ids to session storage and later on, we can paste their content into another product / page content
   */
  saveHierarchyIdsToCopy(type: HierarchyIdsToCopyType, ids: number[]) {
    this._sessionStorage.setItem(type, JSON.stringify(ids));
  }

  getHierarchyIdsToCopy(type: HierarchyIdsToCopyType): number[] | null {
    const item = this._sessionStorage.getItem(type);
    return item ? JSON.parse(item) : null;
  }

  removeHierarchyIdsFromCopy(type: HierarchyIdsToCopyType) {
    this._sessionStorage.removeItem(type);
  }
}
